/**
 * FuecConfig Component
 * Panel de configuracion de parametros FUEC
 */

import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "../../../UserContext";
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  message,
  Divider,
  Space,
  Descriptions,
  Alert,
  Upload,
  Image,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  SettingOutlined,
  BankOutlined,
  FileTextOutlined,
  NumberOutlined,
  UploadOutlined,
  PictureOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";

import {
  fetchFuecConfigList,
  updateFuecConfigBulk,
  clearFuecConfigCache,
  uploadFuecLogo,
  getFuecLogos,
} from "./api";


const FuecConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [logos, setLogos] = useState({ logo_empresa_url: null, logo_ministerio_url: null, logo_colombia_url: null, firma_sello_url: null });
  const [uploadingEmpresa, setUploadingEmpresa] = useState(false);
  const [uploadingMinisterio, setUploadingMinisterio] = useState(false);
  const [uploadingColombia, setUploadingColombia] = useState(false);
  const [uploadingFirmaSello, setUploadingFirmaSello] = useState(false);
  const { user: currentUser } = useUser();

  // Verificación de permisos
  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find((m) => m.key === "FUEC");
      if (!module || module.estado !== "disponible") return false;
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "FUEC" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  const canEdit = hasPermission("GESTIONAR_CONFIGURACION_FUEC");

  // Cargar logos
  const loadLogos = useCallback(async () => {
    try {
      const data = await getFuecLogos();
      setLogos(data);
    } catch (error) {
      console.error("Error cargando logos:", error);
    }
  }, []);

  // Cargar configuraciones
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFuecConfigList();
      const configList = Array.isArray(data) ? data : data.data || [];
      setConfigs(configList);

      // Convertir a objeto para el formulario
      const formValues = {};
      configList.forEach((cfg) => {
        formValues[cfg.key] = cfg.value;
      });
      form.setFieldsValue(formValues);

      // Cargar logos
      await loadLogos();
    } catch (error) {
      console.error("Error cargando configuraciones:", error);
      message.error("Error al cargar configuraciones");
    } finally {
      setLoading(false);
    }
  }, [form, loadLogos]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Guardar configuraciones
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Convertir a array de configs
      const configsToUpdate = Object.entries(values).map(([key, value]) => ({
        key,
        value: value?.toString() || "",
      }));

      await updateFuecConfigBulk(configsToUpdate);
      message.success("Configuraciones guardadas exitosamente");

      // Limpiar cache
      await clearFuecConfigCache();
    } catch (error) {
      console.error("Error guardando configuraciones:", error);
      message.error("Error al guardar configuraciones");
    } finally {
      setSaving(false);
    }
  };

  // Limpiar cache
  const handleClearCache = async () => {
    try {
      await clearFuecConfigCache();
      message.success("Cache limpiado exitosamente");
    } catch (error) {
      console.error("Error limpiando cache:", error);
      message.error("Error al limpiar cache");
    }
  };

  // Manejar upload de logo
  const handleLogoUpload = async (file, tipo) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Solo se permiten archivos de imagen');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('La imagen debe ser menor a 2MB');
      return false;
    }

    // Establecer estado de carga según el tipo
    const setUploadingMap = {
      empresa: setUploadingEmpresa,
      ministerio: setUploadingMinisterio,
      colombia: setUploadingColombia,
      firma_sello: setUploadingFirmaSello,
    };
    setUploadingMap[tipo](true);

    const nombresLogo = {
      empresa: 'empresa',
      ministerio: 'Ministerio de Transporte',
      colombia: 'Colombia Potencia de la Vida',
      firma_sello: 'Firma y Sello',
    };

    try {
      const result = await uploadFuecLogo(file, tipo);
      if (result.success) {
        message.success(`Logo de ${nombresLogo[tipo]} actualizado exitosamente`);
        await loadLogos();
      }
    } catch (error) {
      console.error('Error subiendo logo:', error);
      message.error('Error al subir el logo');
    } finally {
      setUploadingMap[tipo](false);
    }

    return false; // Prevenir upload automático de Ant Design
  };

  // Obtener label amigable para cada key
  const getLabel = (key) => {
    const labels = {
      empresa_nombre: "Razon Social",
      empresa_nit: "NIT",
      empresa_direccion: "Direccion",
      empresa_telefono: "Telefono",
      empresa_correo: "Correo Electronico",
      resolucion_numero: "Numero de Resolucion",
      resolucion_fecha: "Fecha de Resolucion",
      codigo_empresa: "Codigo de Habilitacion",
      logo_path: "Ruta del Logo",
      documento_codigo: "Codigo del Documento",
      documento_fecha: "Fecha del Documento",
      documento_version: "Version del Documento",
    };
    return labels[key] || key;
  };

  // Obtener descripcion para cada key
  const getDescription = (key) => {
    const cfg = configs.find((c) => c.key === key);
    return cfg?.descripcion || "";
  };

  return (
    <div className="fuec-config-container">
      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>Configuracion FUEC</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadConfig}
              loading={loading}
            >
              Recargar
            </Button>
            {canEdit && (
              <>
                <Button onClick={handleClearCache}>Limpiar Cache</Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={saving}
                >
                  Guardar Cambios
                </Button>
              </>
            )}
          </Space>
        }
      >
        <Spin spinning={loading}>
          <Alert
            message="Configuracion del Sistema FUEC"
            description={
              canEdit
                ? "Estos parametros se utilizan para generar los documentos FUEC. Asegurese de que la informacion este actualizada y sea correcta."
                : "Solo tiene permisos de visualizacion. Contacte al administrador para modificar la configuracion."
            }
            type={canEdit ? "info" : "warning"}
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form form={form} layout="vertical" disabled={!canEdit}>
            {/* Datos de la Empresa */}
            <Divider orientation="left">
              <BankOutlined /> Datos de la Empresa
            </Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="empresa_nombre"
                  label={getLabel("empresa_nombre")}
                  tooltip={getDescription("empresa_nombre")}
                  rules={[{ required: true, message: "Campo requerido" }]}
                >
                  <Input placeholder="Nombre de la empresa" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="empresa_nit"
                  label={getLabel("empresa_nit")}
                  tooltip={getDescription("empresa_nit")}
                  rules={[{ required: true, message: "Campo requerido" }]}
                >
                  <Input placeholder="NIT de la empresa" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="empresa_direccion"
                  label={getLabel("empresa_direccion")}
                  tooltip={getDescription("empresa_direccion")}
                >
                  <Input placeholder="Direccion de la empresa" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="empresa_telefono"
                  label={getLabel("empresa_telefono")}
                  tooltip={getDescription("empresa_telefono")}
                >
                  <Input placeholder="Telefono" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="empresa_correo"
                  label={getLabel("empresa_correo")}
                  tooltip={getDescription("empresa_correo")}
                  rules={[{ type: "email", message: "Email invalido" }]}
                >
                  <Input placeholder="Correo electronico" />
                </Form.Item>
              </Col>
            </Row>

            {/* Datos de Resolucion */}
            <Divider orientation="left">
              <FileTextOutlined /> Datos de Habilitacion
            </Divider>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="resolucion_numero"
                  label={getLabel("resolucion_numero")}
                  tooltip={getDescription("resolucion_numero")}
                  rules={[{ required: true, message: "Campo requerido" }]}
                >
                  <Input placeholder="Numero de resolucion" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="resolucion_fecha"
                  label={getLabel("resolucion_fecha")}
                  tooltip={getDescription("resolucion_fecha")}
                  rules={[{ required: true, message: "Campo requerido" }]}
                >
                  <Input placeholder="DD/MM/AAAA" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="codigo_empresa"
                  label={getLabel("codigo_empresa")}
                  tooltip={getDescription("codigo_empresa")}
                  rules={[{ required: true, message: "Campo requerido" }]}
                >
                  <Input placeholder="Codigo de habilitacion" />
                </Form.Item>
              </Col>
            </Row>

            {/* Configuracion Interna del Documento */}
            <Divider orientation="left">
              <FileProtectOutlined /> Configuracion Interna del Documento
            </Divider>

            <Alert
              message="Estos datos aparecen en la cabecera del FUEC"
              description="Se muestran en la esquina superior derecha del documento, junto al logo de la empresa."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="documento_codigo"
                  label={getLabel("documento_codigo")}
                  tooltip="Codigo interno del formato (ej: F-GF-02)"
                  rules={[{ required: true, message: "Campo requerido" }]}
                >
                  <Input placeholder="F-GF-02" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="documento_fecha"
                  label={getLabel("documento_fecha")}
                  tooltip="Fecha de la version del documento (ej: 30-09-2025)"
                  rules={[{ required: true, message: "Campo requerido" }]}
                >
                  <Input placeholder="DD-MM-AAAA" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="documento_version"
                  label={getLabel("documento_version")}
                  tooltip="Numero de version del documento"
                  rules={[{ required: true, message: "Campo requerido" }]}
                >
                  <Input placeholder="2" />
                </Form.Item>
              </Col>
            </Row>

            {/* Logos */}
            <Divider orientation="left">
              <PictureOutlined /> Logos del FUEC
            </Divider>

            <Row gutter={24}>
              <Col xs={24} md={8}>
                <Card
                  size="small"
                  title="Logo de la Empresa"
                  style={{ marginBottom: 16 }}
                >
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    {logos.logo_empresa_url ? (
                      <Image
                        src={logos.logo_empresa_url}
                        alt="Logo Empresa"
                        style={{ maxWidth: 150, maxHeight: 100 }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesAK1wBrwAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAABn9f4XAAAB5klEQVR4Ae3SMQEAAAQAMPLLFnJGxwYAAAAAAAAAAAAA4NoEqgAhEIJACEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIh/AMVAB4AAVUd2QAAAABJRU5ErkJggg=="
                      />
                    ) : (
                      <div style={{
                        width: 150,
                        height: 100,
                        background: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #d9d9d9',
                        margin: '0 auto'
                      }}>
                        <span style={{ color: '#999' }}>Sin logo</span>
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => handleLogoUpload(file, 'empresa')}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingEmpresa}
                        block
                      >
                        Subir Logo Empresa
                      </Button>
                    </Upload>
                  )}
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card
                  size="small"
                  title="Logo del Ministerio de Transporte"
                  style={{ marginBottom: 16 }}
                >
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    {logos.logo_ministerio_url ? (
                      <Image
                        src={logos.logo_ministerio_url}
                        alt="Logo Ministerio"
                        style={{ maxWidth: 150, maxHeight: 100 }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHAK1wBrwAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAABn9f4XAAAB5klEQVR4Ae3SMQEAAAQAMPLLFnJGxwYAAAAAAAAAAAAA4NoEqgAhEIJACEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIh/AMVAB4AAVUd2QAAAABJRU5ErkJggg=="
                      />
                    ) : (
                      <div style={{
                        width: 150,
                        height: 100,
                        background: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #d9d9d9',
                        margin: '0 auto'
                      }}>
                        <span style={{ color: '#999' }}>Sin logo</span>
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => handleLogoUpload(file, 'ministerio')}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingMinisterio}
                        block
                      >
                        Subir Logo Ministerio
                      </Button>
                    </Upload>
                  )}
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card
                  size="small"
                  title="Logo Colombia Potencia de la Vida"
                  style={{ marginBottom: 16 }}
                >
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    {logos.logo_colombia_url ? (
                      <Image
                        src={logos.logo_colombia_url}
                        alt="Logo Colombia"
                        style={{ maxWidth: 150, maxHeight: 100 }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHAK1wBrwAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAABn9f4XAAAB5klEQVR4Ae3SMQEAAAQAMPLLFnJGxwYAAAAAAAAAAAAA4NoEqgAhEIJACEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIh/AMVAB4AAVUd2QAAAABJRU5ErkJggg=="
                      />
                    ) : (
                      <div style={{
                        width: 150,
                        height: 100,
                        background: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #d9d9d9',
                        margin: '0 auto'
                      }}>
                        <span style={{ color: '#999' }}>Sin logo</span>
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => handleLogoUpload(file, 'colombia')}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingColombia}
                        block
                      >
                        Subir Logo Colombia
                      </Button>
                    </Upload>
                  )}
                </Card>
              </Col>
            </Row>

            {/* Firma y Sello */}
            <Row gutter={24} style={{ marginTop: 16 }}>
              <Col xs={24} md={8}>
                <Card
                  size="small"
                  title="Firma y Sello"
                  style={{ marginBottom: 16 }}
                >
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    {logos.firma_sello_url ? (
                      <Image
                        src={logos.firma_sello_url}
                        alt="Firma y Sello"
                        style={{ maxWidth: 150, maxHeight: 100 }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHAK1wBrwAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAABn9f4XAAAB5klEQVR4Ae3SMQEAAAQAMPLLFnJGxwYAAAAAAAAAAAAA4NoEqgAhEIJACEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIhCIUgBEIQCkEIhCAUghAIQSgEIRCCUAhCIAShEIRACEIhCIEQhEIQAiEIhSAEQhAKQQiEIBSCEAhBKAQhEIJQCEIgBKEQhEAIQiEIgRCEQhACIQiFIARCEApBCIQgFIIQCEEoBCEQglAIQiAEoRCEQAhCIQiBEIRCEAIh/AMVAB4AAVUd2QAAAABJRU5ErkJggg=="
                      />
                    ) : (
                      <div style={{
                        width: 150,
                        height: 100,
                        background: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #d9d9d9',
                        margin: '0 auto'
                      }}>
                        <span style={{ color: '#999' }}>Sin imagen</span>
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => handleLogoUpload(file, 'firma_sello')}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingFirmaSello}
                        block
                      >
                        Subir Firma y Sello
                      </Button>
                    </Upload>
                  )}
                  <div style={{ fontSize: '11px', color: '#888', marginTop: 8, textAlign: 'center' }}>
                    Esta imagen aparecera en la seccion de firma del FUEC
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Otros */}
            <Divider orientation="left">
              <NumberOutlined /> Otros Parametros
            </Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="website"
                  label="Sitio Web"
                  tooltip="Sitio web de la empresa"
                >
                  <Input placeholder="https://mogotaxsas.com" />
                </Form.Item>
              </Col>
            </Row>

            <Alert
              message="Codigo QR de Verificacion"
              description="Cada FUEC generado incluye un codigo QR que al ser escaneado abre una vista publica con los datos del documento para verificar su autenticidad."
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </Form>

          {/* Preview de como se vera */}
          <Divider>Vista Previa</Divider>

          <Card size="small" style={{ background: "#f5f5f5" }}>
            <Descriptions
              title="Datos que apareceran en el FUEC"
              column={{ xs: 1, sm: 2, md: 3 }}
              size="small"
            >
              <Descriptions.Item label="Empresa">
                {form.getFieldValue("empresa_nombre") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="NIT">
                {form.getFieldValue("empresa_nit") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Codigo Habilitacion">
                {form.getFieldValue("codigo_empresa") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Resolucion">
                No. {form.getFieldValue("resolucion_numero") || "-"} del{" "}
                {form.getFieldValue("resolucion_fecha") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Direccion">
                {form.getFieldValue("empresa_direccion") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Contacto">
                {form.getFieldValue("empresa_telefono") || "-"} |{" "}
                {form.getFieldValue("empresa_correo") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Codigo Documento">
                {form.getFieldValue("documento_codigo") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha Documento">
                {form.getFieldValue("documento_fecha") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Version Documento">
                {form.getFieldValue("documento_version") || "-"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Spin>
      </Card>
    </div>
  );
};

FuecConfig.propTypes = {};

export default FuecConfig;
